import React, { MutableRefObject, useCallback } from 'react';
import { Box, Button, InputArea, Loader, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/react';
import { ArrowRight } from '@wix/wix-ui-icons-common';
import s from './InputDialog.scss';
import { GradientLiveBorder } from '../GradientLiveBorder/GradientLiveBorder';
import { SuggestionButtons } from './SuggestionButtons';

export interface InputDialogProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  onSend: () => void;
  isLoading: boolean;
  textAreaRef: MutableRefObject<HTMLTextAreaElement | null>;
  handleSuggestionSelect: (value: string) => void;
}

export const InputDialog: React.FC<InputDialogProps> = ({
  prompt,
  onPromptChange,
  onSend,
  isLoading,
  textAreaRef,
  handleSuggestionSelect,
}) => {
  const { t } = useTranslation();
  const [paused, setPaused] = React.useState(false);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setPaused(true);
      onPromptChange(event.target.value);
    },
    [onPromptChange],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        onSend();
      }
    },
    [onSend],
  );

  return (
    <Box
      width="100%"
      direction="vertical"
      className={s.root}
      position="relative"
    >
      <Box direction="vertical" className={s.root2}>
        <GradientLiveBorder paused={paused}>
          <InputArea className={s.inputArea}>
            {({
              dir,
              ref,
              className,
              onInput,
              rows,
              onKeyDown,
              onFocus,
              onBlur,
            }) => (
              <Box width="100%" direction="vertical">
                <textarea
                  ref={(el) => {
                    textAreaRef.current = el;
                    ref(el);
                  }}
                  style={{ height: '74px' }}
                  onInput={onInput}
                  rows={rows}
                  value={prompt}
                  onChange={handleInputChange}
                  placeholder={t('devibe.onboarding.input_placeholder')}
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    handleKeyPress(e);
                    onKeyDown(e);
                  }}
                  dir={dir}
                  onFocus={onFocus}
                  onBlur={onBlur}
                />
                <Box
                  padding="SP2"
                  direction="vertical"
                  align="right"
                  borderTop="1px solid var(--wds-color-divider-primary)"
                >
                  <Button
                    dataHook="send-button"
                    skin="ai"
                    size="small"
                    onClick={() => onSend()}
                    suffixIcon={
                      isLoading ? (
                        <Box
                          direction="vertical"
                          verticalAlign="middle"
                          paddingLeft="SP1"
                        >
                          <Loader size="tiny" />
                        </Box>
                      ) : (
                        <ArrowRight />
                      )
                    }
                  >
                    {t('devibe.onboarding.send')}
                  </Button>
                </Box>
              </Box>
            )}
          </InputArea>
        </GradientLiveBorder>
      </Box>
      <Box margin="5px 12px 0">
        <Text size="small">Try one of these prompts: </Text>
        <SuggestionButtons
          onSelectPrompt={handleSuggestionSelect}
          prompt={prompt}
        />
      </Box>
    </Box>
  );
};
